////PF_Alerts

import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, Tooltip, Checkbox, Button, Space, Card, Input, Select, Pagination, message } from 'antd';
import CompactAlertCard from './CompactAlertCard'; // DAS-2897: jump target from the LLM Advice tab
import { useSelector, useDispatch } from 'react-redux';
import ThreeTablesModal from "./SearchResultModal";
import getUserId from '../functions/getUserId';
import api from '../functions/api';
import epochToDate from '../functions/epochdate';
import OpportunityCheck from "./OpportunityCheck";
import MarketAlerts from "./MarketAlerts"; // Import MarketAlerts component
import { renderAlertEffectiveness } from '../functions/alertClauseService'; // Import renderAlertEffectiveness
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MUIButton, Typography } from '@mui/material';
import InteractiveAlertCard from './InteractiveAlertCard';
import InteractiveCompactAlertCard from './InteractiveCompactAlertCard';
import { compareByUrgency } from '../utils/urgency'; // DAS-2532

const { Search } = Input;


// Redux action to update the 'seen' status
const updateSeenStatus = (id, seen) => {
  return {
    type: 'UPDATE_SEEN_STATUS',
    payload: { id, seen },
  };
};
export const updateFollowedUpStatus = (id, followed_up) => ({
  type: 'UPDATE_FOLLOWED_UP_STATUS',
  payload: { id, followed_up },
});


const PF_Alerts = ({ jumpAlertId = null, onJumpConsumed = null }) => {
  
  const [selectedAlertId, setSelectedAlertId] = useState(null);
  const dispatch = useDispatch();
  const [isMarketAlertsVisible, setIsMarketAlertsVisible] = useState(false);
  const [selectedAlertSubtypeIdentifier, setSelectedAlertSubtypeIdentifier] = useState(null);
  const [isOpportunityCheckVisible, setIsOpportunityCheckVisible] = useState(false);
  
  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [searchText, setSearchText] = useState('');
  const [selectedAlertType, setSelectedAlertType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  // DAS-2489: toggle to show/hide suppressed (seen 7+ days ago) alerts
  const [showSuppressed, setShowSuppressed] = useState(false);

  const handleReinvestmentCheck = (ticker) => {
    setSelectedTicker(ticker); // Set the selected ticker
    setIsOpportunityCheckVisible(true); // Show the OpportunityCheck component
  };
  const harvestAlerts = useSelector(state => state?.portfolio?.data?.pf_alert);
  // console.log("alerts in PF_Alerts", harvestAlerts)

  const selectedAlert = React.useMemo(
  () => Array.isArray(harvestAlerts)
    ? harvestAlerts.find(a => a.ID === selectedAlertId) || null
    : null,
  [harvestAlerts, selectedAlertId]
);

  const alertEffectiveness = useSelector(state => state?.portfolio?.data?.alert_effectiveness_data);

  // DAS-2897: the LLM Advice tab's "follows up alert #N" link lands here.
  // Open that alert's card directly (filters/pagination can't hide it);
  // an archived alert gets an honest message instead of a dead jump.
  useEffect(() => {
    if (!jumpAlertId || !Array.isArray(harvestAlerts)) return;
    const hit = harvestAlerts.find(a => Number(a.ID) === Number(jumpAlertId));
    if (hit) {
      setSelectedAlertId(hit.ID);
    } else {
      message.info(`Alert #${jumpAlertId} is no longer open — it has been archived.`);
    }
    if (onJumpConsumed) onJumpConsumed();
  }, [jumpAlertId, harvestAlerts]);
  // console.log("alert Effectiveness data in PF_Alerts", alertEffectiveness)
  // Sort by seen status first, then by alert_subtype and ticker in alphabetical order

  const [isFPDialogOpen, setIsFPDialogOpen] = useState(false);
  const [fpDialogText, setFPDialogText] = useState('');

  const openFPDialog = (text) => {
    if (!text) return;         // don't open if there's nothing to show
    setFPDialogText(text);
    setIsFPDialogOpen(true);
  };

  const closeFPDialog = () => {
    setIsFPDialogOpen(false);
    setFPDialogText('');
  };


  
  // console.log("Initial harvestAlerts:", harvestAlerts);

  const getAlertAge = (alert) => {
    if (!alert.epoch_initiation) return 0;
    return Math.floor((Date.now() / 1000 - alert.epoch_initiation) / 86400);
  };

  // DAS-2470: Build conflict map — detect tickers with both Accumulate and Harvest alerts
  const conflictMap = useMemo(() => {
    if (!Array.isArray(harvestAlerts)) return {};
    const byTicker = {};
    for (const a of harvestAlerts) {
      const t = (a.ticker || '').toLowerCase();
      const type = (a.alert_type || '').toLowerCase();
      if (!t) continue;
      if (!byTicker[t]) byTicker[t] = {};
      if (type === 'accumulate' || type === 'step in') {
        const conf = parseFloat(a.pf_final_confidence_score);
        if (!byTicker[t].accumulate || conf > byTicker[t].accumulate)
          byTicker[t].accumulate = Number.isFinite(conf) ? conf : 0;
      } else if (type === 'harvest') {
        const conf = parseFloat(a.pf_final_confidence_score);
        if (!byTicker[t].harvest || conf > byTicker[t].harvest)
          byTicker[t].harvest = Number.isFinite(conf) ? conf : 0;
      }
    }
    // Only keep tickers with BOTH signals
    const conflicts = {};
    for (const [t, v] of Object.entries(byTicker)) {
      if (v.accumulate && v.harvest) conflicts[t] = v;
    }
    return conflicts;
  }, [harvestAlerts]);

  const sortedHarvestAlerts = Array.isArray(harvestAlerts)
  ? [...harvestAlerts].sort((a, b) => {
      // Always: unseen first
      const seenA = a.seen === null ? 0 : Number(a.seen);
      const seenB = b.seen === null ? 0 : Number(b.seen);
      if (seenA !== seenB) return seenA - seenB;

      // User-selected sort
      switch (sortBy) {
        case 'alert_type':
          return (a.alert_type || '').localeCompare(b.alert_type || '');
        case 'age_desc':
          return getAlertAge(b) - getAlertAge(a);
        case 'age_asc':
          return getAlertAge(a) - getAlertAge(b);
        case 'alignment_desc':
          return (b.LLM_analysis_alignment_score ?? 0) - (a.LLM_analysis_alignment_score ?? 0);
        case 'scenario_desc':
          // DAS-2665: scenario fit — alerts agreeing with a rising named risk (+1)
          // first, colliding ones (-1) last; ties fall back to urgency.
          return ((b.scenario_alignment ?? 0) - (a.scenario_alignment ?? 0)) || compareByUrgency(a, b);
        case 'fp_risk_asc':
          return (a.LLM_false_positive_score ?? 10) - (b.LLM_false_positive_score ?? 10);
        case 'confirmation_desc':
          return (parseInt(b.pf_weighted_confirmation_percentage, 10) || 0) - (parseInt(a.pf_weighted_confirmation_percentage, 10) || 0);
        case 'confidence_desc':
          return (b.pf_final_confidence_score ?? 0) - (a.pf_final_confidence_score ?? 0);
        case 'opportunity_desc':
          return (b.opportunity_factor ?? 0) - (a.opportunity_factor ?? 0);
        case 'ticker_asc':
          return (a.ticker || '').localeCompare(b.ticker || '');
        case 'company_asc':
          return (a.company || '').localeCompare(b.company || '');
        case 'urgency_desc':
        default:
          // DAS-2532: urgency is the default sort (high -> low; ties: confidence, then
          // ticker). Supersedes DAS-2487 fresh-first — age/freshness already live inside
          // the opportunity component of urgency, so urgency-first is a superset.
          return compareByUrgency(a, b);
      }
    })
  : [];

  // DAS-2489: Split alerts into active vs suppressed (seen 7+ days ago)
  const { activeAlerts, suppressedAlerts } = useMemo(() => {
    const active = [];
    const suppressed = [];
    for (const alert of sortedHarvestAlerts) {
      if (Number(alert.suppressed) === 1) {
        suppressed.push(alert);
      } else {
        active.push(alert);
      }
    }
    return { activeAlerts: active, suppressedAlerts: suppressed };
  }, [sortedHarvestAlerts]);

  // When showSuppressed is toggled, use the appropriate list
  const displayAlerts = showSuppressed ? suppressedAlerts : activeAlerts;

  // Filter data based on search and filters
  const filteredData = displayAlerts.filter(alert => {
    const matchesSearch = !searchText ||
      alert.company?.toLowerCase().includes(searchText.toLowerCase()) ||
      alert.ticker?.toLowerCase().includes(searchText.toLowerCase()) ||
      alert.pf_alert_text?.toLowerCase().includes(searchText.toLowerCase());

    const matchesAlertType = selectedAlertType === 'all' || alert.alert_type === selectedAlertType;
    const matchesCategory = selectedCategory === 'all' || alert.Category === selectedCategory;

    return matchesSearch && matchesAlertType && matchesCategory;
  });

  // Get current page data
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Get unique alert types and categories for filters (from all alerts, not just displayed)
  const alertTypes = [...new Set(sortedHarvestAlerts.map(alert => alert.alert_type))];
  const categories = [...new Set(sortedHarvestAlerts.map(alert => alert.Category))];

  const userId = getUserId();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState(null);

  const handleRowClick = (ticker) => {
    setSelectedTicker(ticker);
    setIsModalVisible(true);
  };

  const handleCheckboxChange = async (id, currentStatus, followed_up) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await api.post('/pf_alert_check_update', { id, seen: newStatus });

      dispatch({
        type: 'UPDATE_SEEN_STATUS',
        payload: { id, seen: newStatus },
      });

      // Force re-fetch alerts from Redux store to trigger re-render
      dispatch({ type: 'REFRESH_ALERTS' });

    } catch (error) {
      console.error('Failed to update seen status', error);
    }
  };

  const handleFollowedUpChange = async (id, seen, currentFollowedUp) => {
    try {
      const newFollowedUp = currentFollowedUp === 1 ? 0 : 1;
      await api.post('/pf_alert_check_update', { id, followed_up: newFollowedUp });

      dispatch(updateFollowedUpStatus(id, newFollowedUp));

      // Force re-fetch alerts from Redux store to trigger re-render
      dispatch({ type: 'REFRESH_ALERTS' });

    } catch (error) {
      console.error('Failed to update followed up status', error);
    }
  };

  const handleOpenMarketAlerts = (alertSubtypeIdentifier) => {
    setSelectedAlertSubtypeIdentifier(alertSubtypeIdentifier);
    setIsMarketAlertsVisible(true);
  };

  const handleCloseMarketAlerts = () => {
    setIsMarketAlertsVisible(false);
    setSelectedAlertSubtypeIdentifier(null);
  };

  const formatAlertText = (text) => {
    if (!text) return null;

    const becauseIndex = text.indexOf('because:');
    const formattedText = becauseIndex !== -1
      ? `${text.substring(0, becauseIndex + 8)}\n${text
          .substring(becauseIndex + 8)
          .replace(/ - /g, '\n- ')
          .replace(/\n+/g, '\n')
          .trim()}`
      : text;

    return formattedText.split('\n').map((line, index) => {
      if (line.startsWith('- (initiated') || line.startsWith('- (updated')) {
        return (
          <div
            key={index}
            style={{
              color: 'gray',
              fontStyle: 'italic',
              fontSize: '10px',
            }}
          >
            {line.replace(/^- /, '')}
          </div>
        );
      } else if (line.startsWith('-')) {
        return (
          <div key={index} className="bullet-container">
            {line.replace(/^- /, '')}
          </div>
        );
      }
      return <div key={index}>{line}</div>;
    });
  };

  return (
    <>
      <div style={{ padding: '20px' }}>
        <h2>{showSuppressed ? 'Portfolio Alerts — Seen (Suppressed)' : 'Portfolio Alerts'}</h2>
        
        {/* Filters and Search */}
        <Space style={{ marginBottom: '20px', width: '100%' }} direction="vertical" size="middle">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Search
                placeholder="Search by company, ticker, or alert text..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by Alert Type"
                value={selectedAlertType}
                onChange={setSelectedAlertType}
                style={{ width: '100%' }}
              >
                <Select.Option value="all">All Alert Types</Select.Option>
                {alertTypes.map(type => (
                  <Select.Option key={type} value={type}>{type}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by Category"
                value={selectedCategory}
                onChange={setSelectedCategory}
                style={{ width: '100%' }}
              >
                <Select.Option value="all">All Categories</Select.Option>
                {categories.map(category => (
                  <Select.Option key={category} value={category}>{category}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                value={sortBy}
                onChange={(val) => { setSortBy(val); setCurrentPage(1); }}
                style={{ width: '100%' }}
              >
                <Select.Option value="default">Sort: Urgency (default)</Select.Option>
                <Select.Option value="alert_type">Sort: Alert Type</Select.Option>
                <Select.Option value="age_desc">Sort: Age (oldest first)</Select.Option>
                <Select.Option value="age_asc">Sort: Age (newest first)</Select.Option>
                <Select.Option value="alignment_desc">Sort: Alignment (high→low)</Select.Option>
                <Select.Option value="scenario_desc">Sort: Scenario fit (aligned→colliding)</Select.Option>
                <Select.Option value="fp_risk_asc">Sort: FP Risk (low→high)</Select.Option>
                <Select.Option value="confirmation_desc">Sort: Confirmation (high→low)</Select.Option>
                <Select.Option value="confidence_desc">Sort: Weighted Confidence (high→low)</Select.Option>
                <Select.Option value="opportunity_desc">Sort: Opportunity (high→low)</Select.Option>
                <Select.Option value="ticker_asc">Sort: Ticker (A→Z)</Select.Option>
                <Select.Option value="company_asc">Sort: Company (A→Z)</Select.Option>
              </Select>
            </Col>
          </Row>
          <Row justify="space-between" align="middle">
            <Col>
              {/* DAS-2489: Toggle between active and suppressed alerts */}
              {!showSuppressed ? (
                suppressedAlerts.length > 0 && (
                  <Button
                    type="link"
                    onClick={() => { setShowSuppressed(true); setCurrentPage(1); }}
                    style={{ padding: 0, color: '#888' }}
                  >
                    Review seen alerts ({suppressedAlerts.length})
                  </Button>
                )
              ) : (
                <Button
                  type="link"
                  onClick={() => { setShowSuppressed(false); setCurrentPage(1); }}
                  style={{ padding: 0, color: '#1890ff' }}
                >
                  ← Back to active alerts ({activeAlerts.length})
                </Button>
              )}
            </Col>
            <Col>
              <span style={{ color: '#666' }}>
                {showSuppressed ? 'Suppressed: ' : ''}Showing {currentData.length} of {filteredData.length} alerts
              </span>
            </Col>
          </Row>
        </Space>

        {/* Cards Grid */}
        <Row gutter={[20, 20]}>
          {currentData.map((alert) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={alert.ID}>
              <InteractiveCompactAlertCard
                alert={alert}
                alertEffectiveness={alertEffectiveness}
                onCheckboxChange={handleCheckboxChange}
                onFollowedUpChange={handleFollowedUpChange}
                onReinvestmentCheck={handleReinvestmentCheck}
                openFPDialog={openFPDialog}
                conflictInfo={conflictMap[(alert.ticker || '').toLowerCase()]}
              />
            </Col>
          ))}
        </Row>

        {/* Pagination */}
        {filteredData.length > pageSize && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} alerts`}
            />
          </div>
        )}
      </div>

      {isModalVisible && (
        <ThreeTablesModal 
          tickerValue={selectedTicker} 
          open={isModalVisible} 
          onClose={() => setIsModalVisible(false)} 
          userId={userId} 
        />
      )}
      {isOpportunityCheckVisible && (
          <OpportunityCheck 
            ticker={selectedTicker} 
            onClose={() => setIsOpportunityCheckVisible(false)} 
          />
        )}
      {isMarketAlertsVisible && (
        <MarketAlerts
          isVisible={isMarketAlertsVisible}
          onClose={handleCloseMarketAlerts}
          alertSubtypeIdentifier={selectedAlertSubtypeIdentifier} // Pass the selected identifier(s)
        />
      )}

{selectedAlert && (
  <Dialog
    open={true}
    onClose={() => setSelectedAlertId(null)}
    fullWidth
    maxWidth="md"
  >
    {/* DAS-2897: revived — this dialog was empty dead code; the jump from the
        LLM Advice tab needs a card that works regardless of filters/pages. */}
    <CompactAlertCard
      alert={selectedAlert}
      alertEffectiveness={alertEffectiveness}
      onClose={() => setSelectedAlertId(null)}
      onCheckboxChange={handleCheckboxChange}
      onFollowedUpChange={handleFollowedUpChange}
      onReinvestmentCheck={handleReinvestmentCheck}
      openFPDialog={openFPDialog}
    />
  </Dialog>
)}



      <Dialog
  open={isFPDialogOpen}
  onClose={closeFPDialog}
  fullWidth
  maxWidth="md"
>
  <DialogTitle>LLM False Positive Explanation</DialogTitle>
  <DialogContent dividers>
    <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
      {fpDialogText}
    </Typography>
  </DialogContent>
  <DialogActions>
    <MUIButton onClick={closeFPDialog} variant="contained">Close</MUIButton>
  </DialogActions>
</Dialog>

    </>
  );
};

export default PF_Alerts;

